<template>
  <div class="activity-list">
    <div class="content-wrapper">
      <div class="h-full">
        <!-- 状态标签组 -->
        <XzTagGroup
          :data="statusOptions"
          :active="searchForm.status"
          @handleClick="(v) => handleStatusChange(v)"
        >
          <span class="title">活动状态</span>
        </XzTagGroup>

        <!-- 主表格 -->
        <div class="table-wrapper" :class="{ fullscreen: isFullScreen }">
          <xz-table
            ref="xzTable"
            :columns="columns"
            :data="tableData"
            :searchAttrs="searchAttrs"
            :searchVal.sync="searchForm.name"
            :toolsAttrs="toolsAttrs"
            :scroll="{ x: '100%', y: scrollHeight }"
            @search="handleSearch"
            @refresh="handleReset"
            @fullScreen="handleFullScreen"
            rowKey="id"
          >
            <template #buttons>
              <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleCreate">新建活动</el-button>
            </template>

            <!-- 活动编号列 -->
            <span slot="activityNo" slot-scope="text, record">
              <span class="link-text" @click="handleView(record)">{{ text }}</span>
            </span>

            <!-- 状态列 -->
            <span slot="statusDesc" slot-scope="text, record">
              <span :class="['status-tag', 'status-' + record.status]">{{ text }}</span>
            </span>

            <!-- 时间列 -->
            <span slot="dateRange" slot-scope="_, record">
              <span v-if="record.isPermanent">永久有效</span>
              <span v-else>{{ formatDate(record.startDate) }} ~ {{ formatDate(record.endDate) }}</span>
            </span>

            <!-- 操作列 -->
            <span slot="action" slot-scope="_, record">
              <a @click="handleView(record)">详情</a>
              <a-divider type="vertical" />
              <a @click="handlePlan(record)">活动策划</a>
              <template v-if="record.status === 0">
                <a-divider type="vertical" />
                <a @click="handleOpen(record)">开启</a>
              </template>
              <template v-if="record.status === 1">
                <a-divider type="vertical" />
                <a-popconfirm title="确定要关闭该活动吗？" @confirm="handleClose(record)">
                  <a class="danger-link">关闭</a>
                </a-popconfirm>
              </template>
              <template v-if="record.status === 2">
                <a-divider type="vertical" />
                <a @click="handleOpen(record)">开启</a>
              </template>
            </span>
          </xz-table>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper">
      <a-pagination
        :current="page.pageNum"
        :total="total"
        :show-total="(t) => `共 ${t} 条`"
        show-size-changer
        :pageSizeOptions="['10', '20', '30', '50']"
        show-quick-jumper
        :pageSize.sync="page.pageSize"
        @change="handlePageChange"
        @showSizeChange="handleSizeChange"
      />
    </div>

    <!-- 新建活动弹窗 -->
    <a-modal
      :visible="createVisible"
      title="新建活动"
      :width="560"
      :confirmLoading="submitLoading"
      @ok="handleCreateSubmit"
      @cancel="handleCreateCancel"
    >
      <a-form-model
        ref="createForm"
        :model="createForm"
        :rules="createRules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item label="活动标题" prop="name">
          <a-input v-model="createForm.name" placeholder="请输入活动标题" :maxLength="50" />
        </a-form-model-item>
        <a-form-model-item label="活动有效期" prop="isPermanent">
          <a-radio-group v-model="createForm.isPermanent">
            <a-radio :value="false">指定时间段</a-radio>
            <a-radio :value="true">永久有效</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item v-if="!createForm.isPermanent" label="时间段" prop="dateRange">
          <a-range-picker
            v-model="createForm.dateRange"
            style="width: 100%"
            :placeholder="['开始日期', '结束日期']"
            :disabled-date="disabledDate"
          />
        </a-form-model-item>
        <a-form-model-item label="活动描述" prop="description">
          <a-textarea
            v-model="createForm.description"
            :rows="3"
            placeholder="请输入活动描述（选填）"
            :maxLength="500"
          />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import xzTable from '@/components/xiaozi-ui/xz-table/src/table.vue';
import XzTagGroup from '@/components/xiaozi-ui/xz-tag-group';
import {
  activityPageList,
  activityCreate,
  activityUpdate,
  activityOpen,
  activityClose,
} from '@/api/activity.js';

const STATUS_OPTIONS = [
  { label: '全部', value: '' },
  { label: '未开始', value: 0 },
  { label: '进行中', value: 1 },
  { label: '已结束', value: 2 },
];

export default {
  name: 'ActivityList',
  components: { xzTable, XzTagGroup },
  data () {
    return {
      tableData: [],
      total: 0,
      page: { pageNum: 1, pageSize: 10 },
      searchForm: { status: '', name: '' },
      scrollHeight: 'calc(100vh - 350px)',
      isFullScreen: false,

      statusOptions: STATUS_OPTIONS,

      columns: [
        { title: '活动编号', dataIndex: 'activityNo', key: 'activityNo', width: 160, scopedSlots: { customRender: 'activityNo' } },
        { title: '活动标题', dataIndex: 'name', key: 'name', width: 200, ellipsis: true },
        { title: '状态', dataIndex: 'statusDesc', key: 'statusDesc', width: 100, scopedSlots: { customRender: 'statusDesc' } },
        { title: '有效期', key: 'dateRange', width: 220, scopedSlots: { customRender: 'dateRange' } },
        { title: '活动描述', dataIndex: 'description', key: 'description', width: 200, ellipsis: true, customRender: (t) => t || '--' },
        { title: '创建人', dataIndex: 'createUserName', key: 'createUserName', width: 100 },
        { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 160, customRender: (t) => t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '--' },
        { title: '操作', key: 'action', width: 240, fixed: 'right', scopedSlots: { customRender: 'action' } },
      ],

      searchAttrs: { show: true, placeholder: '搜索活动标题', data: 'name' },

      // 新建弹窗
      createVisible: false,
      submitLoading: false,
      editRow: null,
      createForm: {
        name: '',
        isPermanent: false,
        dateRange: [],
        description: '',
      },
      createRules: {
        name: [{ required: true, message: '请输入活动标题', trigger: 'blur' }],
        isPermanent: [{ required: true, message: '请选择有效期类型', trigger: 'change' }],
        dateRange: [
          {
            validator: (rule, value, cb) => {
              if (!this.createForm.isPermanent && (!value || value.length !== 2)) {
                cb('请选择活动时间段');
              } else {
                cb();
              }
            },
            trigger: 'change',
          },
        ],
      },
    };
  },

  computed: {
    toolsAttrs () {
      return {
        refresh: true,
        zoom: true,
        fullScreen: this.isFullScreen,
        custom: false,
        advancedSearch: false,
      };
    },
  },

  created () {
    this.handleSearch();
  },

  methods: {
    disabledDate (current) {
      return current && current.valueOf() < dayjs().startOf('day').valueOf();
    },

    async handleSearch () {
      const params = {
        ...this.page,
        status: this.searchForm.status === '' ? undefined : this.searchForm.status,
        name: this.searchForm.name || undefined,
        orderBy: [{ orderByProperty: 'createTime', orderByType: 'DESC' }],
      };
      const { body } = await activityPageList(params);
      this.tableData = body?.list || [];
      this.total = (body?.total || 0) * 1;
    },

    handleReset () {
      this.searchForm = { status: '', name: '' };
      this.page = { pageNum: 1, pageSize: 10 };
      this.handleSearch();
    },

    handleStatusChange (item) {
      this.searchForm.status = item.value;
      this.page.pageNum = 1;
      this.handleSearch();
    },

    handlePageChange (num) {
      this.page.pageNum = num;
      this.handleSearch();
    },

    handleSizeChange (_, size) {
      this.page.pageSize = size;
      this.page.pageNum = 1;
      this.handleSearch();
    },

    handleFullScreen (val) {
      this.isFullScreen = val;
    },

    handleView (record) {
      this.$router.push({ name: 'ACTIVITY_DETAIL', query: { id: record.id } });
    },

    handlePlan (record) {
      this.$router.push({ name: 'ACTIVITY_PLAN', query: { id: record.id } });
    },

    handleEdit (record) {
      this.editRow = record;
      this.createForm = {
        name: record.name,
        isPermanent: record.isPermanent,
        dateRange: record.isPermanent ? [] : [dayjs(record.startDate), dayjs(record.endDate)],
        description: record.description || '',
      };
      this.createVisible = true;
    },

    async handleOpen (record) {
      await activityOpen({ id: record.id });
      this.$message.success('活动已开启');
      this.handleSearch();
    },

    async handleClose (record) {
      await activityClose({ id: record.id });
      this.$message.success('活动已关闭');
      this.handleSearch();
    },

    handleCreate () {
      this.editRow = null;
      this.createForm = { name: '', isPermanent: false, dateRange: [], description: '' };
      this.createVisible = true;
    },

    handleCreateCancel () {
      this.createVisible = false;
      this.$refs.createForm && this.$refs.createForm.resetFields();
    },

    async handleCreateSubmit () {
      this.$refs.createForm.validate(async (valid) => {
        if (!valid) return;
        this.submitLoading = true;
        try {
          const params = {
            name: this.createForm.name,
            isPermanent: this.createForm.isPermanent,
            startDate: this.createForm.isPermanent ? undefined : this.createForm.dateRange[0].valueOf(),
            endDate: this.createForm.isPermanent ? undefined : this.createForm.dateRange[1].valueOf(),
            description: this.createForm.description,
          };
          if (this.editRow) {
            await activityUpdate({ id: this.editRow.id, ...params });
            this.$message.success('修改成功');
          } else {
            await activityCreate(params);
            this.$message.success('创建成功');
          }
          this.createVisible = false;
          this.$refs.createForm.resetFields();
          this.handleSearch();
        } finally {
          this.submitLoading = false;
        }
      });
    },

    formatDate (date) {
      return date ? dayjs(date).format('YYYY-MM-DD') : '--';
    },
  },
};
</script>

<style lang="scss" scoped>
.activity-list {
  height: 100%;
  display: flex;
  flex-direction: column;

  .content-wrapper {
    flex: 1;
    overflow: hidden;
    padding: 0 0 8px;

    .h-full {
      height: 100%;
      background-color: #fff;
      display: flex;
      flex-direction: column;

      .title {
        font-size: 14px;
        font-weight: 600;
        padding-right: 2em;
      }

      .table-wrapper {
        padding: 16px 12px;
        flex: 1;
        overflow: auto;
      }
    }
  }

  .table-wrapper.fullscreen {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    z-index: 100;
    background: #ffffff;
  }

  .pagination-wrapper {
    background: #fff;
    padding: 8px 12px;
    text-align: right;
  }

  // 活动编号链接
  .link-text {
    color: #237ffa;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  // 状态标签
  @mixin tag_round() {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
  }

  .status-tag {
    @include tag_round();
  }

  .status-0 {
    background-color: #f2f4f7;
    color: #1f2733;
  }

  .status-1 {
    background-color: #e2f3fe;
    color: #237ffa;
  }

  .status-2 {
    background-color: #f2f4f7;
    color: #929aa6;
  }

  // 危险操作链接
  .danger-link {
    color: #ff4d4f;
  }

  // 滚动条
  /deep/ .ant-table-body::-webkit-scrollbar {
    height: 12px;
    width: 12px;
  }

  /deep/ .ant-table-body::-webkit-scrollbar-thumb {
    border-radius: 8px;
    box-shadow: inset 0 0 5px #c9ced6;
    background: #c9ced6;
  }

  /deep/ .ant-table-body::-webkit-scrollbar-thumb:hover {
    background: #c9ced6;
  }
}
</style>
